import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Badge,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  Select,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Textarea,
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiMinus } from 'react-icons/fi';
import { projectApi } from '../services/api';
import { v4 } from 'uuid';

const BlockConfigManager = ({ projectId }) => {
  const toast = useToast();
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configToDelete, setConfigToDelete] = useState(null);
  
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // ブロック設定一覧の取得
  const fetchConfigs = async () => {
    setIsLoading(true);
    try {
      const response = await projectApi.get(`/${projectId}/ocr-configs`);
      setConfigs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch block configs:', error);
      toast({
        title: 'ブロック設定の取得に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [projectId]);

  // 設定詳細の表示
  const handleView = (config) => {
    setSelectedConfig(config);
    onViewOpen();
  };

  // 設定編集の開始
  const handleEdit = (config) => {
    // 編集用にコピーを作成
    setEditingConfig({
      ...config,
      blocks: config.blocks ? [...config.blocks.map(block => ({
        ...block,
        items: [...block.items]
      }))] : []
    });
    onEditOpen();
  };

  // 編集内容の保存
  const handleSaveEdit = async () => {
    try {
      // バックエンドAPIで設定を更新
      await projectApi.put(`/${projectId}/ocr-configs/${editingConfig.id}`, {
        name: editingConfig.name,
        blocks: editingConfig.blocks,
        customPrompt: editingConfig.customPrompt || ''
      });

      toast({
        title: '設定が更新されました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await fetchConfigs();
      onEditClose();
    } catch (error) {
      console.error('Failed to update config:', error);
      toast({
        title: '設定の更新に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 設定削除の確認
  const handleDeleteConfirm = (config) => {
    setConfigToDelete(config);
    onDeleteOpen();
  };

  // 設定削除の実行
  const handleDelete = async () => {
    try {
      await projectApi.delete(`/${projectId}/ocr-configs/${configToDelete.id}`);
      
      toast({
        title: '設定が削除されました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      await fetchConfigs();
      onDeleteClose();
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast({
        title: '設定の削除に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // ブロック追加
  const addBlock = () => {
    const newBlock = {
      id: v4(),
      blockName: "新しいブロック",
      blockType: "header",
      items: [{ id: v4(), itemName: "", columnName: "" }]
    };
    setEditingConfig(prev => ({
      ...prev,
      blocks: [...prev.blocks, newBlock]
    }));
  };

  // ブロック削除
  const deleteBlock = (blockId) => {
    setEditingConfig(prev => ({
      ...prev,
      blocks: prev.blocks.filter(block => block.id !== blockId)
    }));
  };

  // ブロック名更新
  const updateBlockName = (blockId, name) => {
    setEditingConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId ? { ...block, blockName: name } : block
      )
    }));
  };

  // ブロックタイプ更新
  const updateBlockType = (blockId, type) => {
    setEditingConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId ? { ...block, blockType: type } : block
      )
    }));
  };

  // アイテム追加
  const addItemToBlock = (blockId) => {
    const newItem = { id: v4(), itemName: "", columnName: "" };
    setEditingConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId 
          ? { ...block, items: [...block.items, newItem] }
          : block
      )
    }));
  };

  // アイテム削除
  const deleteItemFromBlock = (blockId, itemId) => {
    setEditingConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId 
          ? { ...block, items: block.items.filter(item => item.id !== itemId) }
          : block
      )
    }));
  };

  // アイテム更新
  const updateItemInBlock = (blockId, itemId, field, value) => {
    setEditingConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => 
        block.id === blockId 
          ? {
              ...block, 
              items: block.items.map(item => 
                item.id === itemId ? { ...item, [field]: value } : item
              )
            }
          : block
      )
    }));
  };

  if (isLoading) {
    return (
      <Box p={6}>
        <Text>読み込み中...</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md">ブロック設定管理</Heading>
        <Text fontSize="sm" color="gray.600">
          {configs.length} 件の設定
        </Text>
      </Flex>

      {configs.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          保存されたブロック設定がありません。OCR処理画面で設定を作成してください。
        </Alert>
      ) : (
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>設定名</Th>
                <Th>ブロック数</Th>
                <Th>作成日時</Th>
                <Th>アクション</Th>
              </Tr>
            </Thead>
            <Tbody>
              {configs.map((config) => (
                <Tr key={config.id}>
                  <Td>
                    <Text fontWeight="bold">{config.name}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme="blue">
                      {config.blocks ? config.blocks.length : 0} ブロック
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {new Date(config.createdAt).toLocaleString('ja-JP')}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="詳細表示">
                        <IconButton
                          icon={<FiEye />}
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(config)}
                          aria-label="詳細表示"
                        />
                      </Tooltip>
                      <Tooltip label="編集">
                        <IconButton
                          icon={<FiEdit />}
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleEdit(config)}
                          aria-label="編集"
                        />
                      </Tooltip>
                      <Tooltip label="削除">
                        <IconButton
                          icon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDeleteConfirm(config)}
                          aria-label="削除"
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* 詳細表示モーダル */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>設定詳細: {selectedConfig?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedConfig && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={2}>カスタムプロンプト:</Text>
                  <Text 
                    bg={useColorModeValue('gray.50', 'gray.700')} 
                    p={3} 
                    borderRadius="md"
                    whiteSpace="pre-wrap"
                  >
                    {selectedConfig.customPrompt || 'なし'}
                  </Text>
                </Box>
                
                <Box>
                  <Text fontWeight="bold" mb={2}>ブロック設定:</Text>
                  {selectedConfig.blocks && selectedConfig.blocks.length > 0 ? (
                    <VStack spacing={3} align="stretch">
                      {selectedConfig.blocks.map((block, index) => (
                        <Card key={block.id || index} variant="outline">
                          <CardHeader py={3}>
                            <Flex justify="space-between" align="center">
                              <Text fontWeight="bold">{block.blockName}</Text>
                              <Badge colorScheme={block.blockType === 'table' ? 'green' : 'blue'}>
                                {block.blockType === 'table' ? 'テーブル' : 'ヘッダー'}
                              </Badge>
                            </Flex>
                          </CardHeader>
                          <CardBody pt={0}>
                            {block.items && block.items.length > 0 ? (
                              <TableContainer>
                                <Table size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>項目名</Th>
                                      <Th>カラム名</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {block.items.map((item, itemIndex) => (
                                      <Tr key={item.id || itemIndex}>
                                        <Td>{item.itemName || '-'}</Td>
                                        <Td>{item.columnName || '-'}</Td>
                                      </Tr>
                                    ))}
                                  </Tbody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Text color="gray.500">項目が設定されていません</Text>
                            )}
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  ) : (
                    <Text color="gray.500">ブロックが設定されていません</Text>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>閉じる</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>設定編集: {editingConfig?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingConfig && (
              <VStack spacing={6} align="stretch">
                <FormControl>
                  <FormLabel>設定名</FormLabel>
                  <Input
                    value={editingConfig.name || ''}
                    onChange={(e) => setEditingConfig(prev => ({ ...prev, name: e.target.value }))}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>カスタムプロンプト</FormLabel>
                  <Textarea
                    value={editingConfig.customPrompt || ''}
                    onChange={(e) => setEditingConfig(prev => ({ ...prev, customPrompt: e.target.value }))}
                    rows={4}
                  />
                </FormControl>

                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Text fontWeight="bold">ブロック設定</Text>
                    <Button
                      size="sm"
                      leftIcon={<FiPlus />}
                      colorScheme="blue"
                      onClick={addBlock}
                    >
                      ブロックを追加
                    </Button>
                  </Flex>

                  {editingConfig.blocks && editingConfig.blocks.length > 0 ? (
                    <VStack spacing={4} align="stretch">
                      {editingConfig.blocks.map((block) => (
                        <Card key={block.id} variant="outline">
                          <CardBody>
                            <VStack spacing={3} align="stretch">
                              <Flex gap={3} align="end">
                                <FormControl>
                                  <FormLabel fontSize="sm">ブロック名</FormLabel>
                                  <Input
                                    value={block.blockName}
                                    onChange={(e) => updateBlockName(block.id, e.target.value)}
                                    placeholder="ブロック名を入力"
                                    size="sm"
                                  />
                                </FormControl>
                                <FormControl>
                                  <FormLabel fontSize="sm">ブロックタイプ</FormLabel>
                                  <Select
                                    value={block.blockType}
                                    onChange={(e) => updateBlockType(block.id, e.target.value)}
                                    size="sm"
                                  >
                                    <option value="header">ヘッダー</option>
                                    <option value="table">テーブル</option>
                                  </Select>
                                </FormControl>
                                <IconButton
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => deleteBlock(block.id)}
                                  aria-label="ブロック削除"
                                />
                              </Flex>

                              <Box>
                                <Flex justify="space-between" align="center" mb={2}>
                                  <Text fontSize="sm" fontWeight="bold">項目設定</Text>
                                  <Button
                                    size="xs"
                                    leftIcon={<FiPlus />}
                                    onClick={() => addItemToBlock(block.id)}
                                  >
                                    項目追加
                                  </Button>
                                </Flex>

                                {block.items && block.items.length > 0 ? (
                                  <VStack spacing={2} align="stretch">
                                    {block.items.map((item) => (
                                      <Flex key={item.id} gap={2} align="end">
                                        <FormControl>
                                          <Input
                                            value={item.itemName}
                                            onChange={(e) => updateItemInBlock(block.id, item.id, 'itemName', e.target.value)}
                                            placeholder="項目名"
                                            size="sm"
                                          />
                                        </FormControl>
                                        <FormControl>
                                          <Input
                                            value={item.columnName}
                                            onChange={(e) => updateItemInBlock(block.id, item.id, 'columnName', e.target.value)}
                                            placeholder="カラム名"
                                            size="sm"
                                          />
                                        </FormControl>
                                        <IconButton
                                          icon={<FiMinus />}
                                          size="sm"
                                          variant="outline"
                                          onClick={() => deleteItemFromBlock(block.id, item.id)}
                                          aria-label="項目削除"
                                        />
                                      </Flex>
                                    ))}
                                  </VStack>
                                ) : (
                                  <Text fontSize="sm" color="gray.500">項目がありません</Text>
                                )}
                              </Box>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  ) : (
                    <Text color="gray.500">ブロックがありません</Text>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              キャンセル
            </Button>
            <Button colorScheme="blue" onClick={handleSaveEdit}>
              保存
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              設定を削除
            </AlertDialogHeader>

            <AlertDialogBody>
              設定「{configToDelete?.name}」を削除しますか？
              この操作は元に戻すことができません。
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                キャンセル
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                削除
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default BlockConfigManager;